/**
 * AppRouter (CONTRACTS.md §2, master spec §5). Mounts every path in PATHS
 * plus LEGACY_REDIRECTS. Route strings come from ./paths only.
 *
 * Page components are built in parallel by other workstreams and are imported
 * here exactly per the module table in CONTRACTS.md §6.
 */
import type { ReactNode } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { AppShell } from '@/components/navigation'
import { LEGACY_REDIRECTS, PATHS } from './paths'
import { RequireAuth, SessionLoadingScreen, portalHomeForRole } from './RequireAuth'
import { useAuth } from '@/features/auth/useAuth'

// Auth & public pages (workstream C — this workspace)
import WelcomePage from '@/features/auth/pages/WelcomePage'
import OnboardingPage from '@/features/auth/pages/OnboardingPage'
import RolePage from '@/features/auth/pages/RolePage'
import ClientLoginPage from '@/features/auth/pages/ClientLoginPage'
import ClientRegisterPage from '@/features/auth/pages/ClientRegisterPage'
import CaregiverLoginPage from '@/features/auth/pages/CaregiverLoginPage'
import CaregiverRegisterPage from '@/features/auth/pages/CaregiverRegisterPage'
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage'
import ResetPasswordPage from '@/features/auth/pages/ResetPasswordPage'
import AuthCallbackPage from '@/features/auth/pages/AuthCallbackPage'
import NotFoundPage from '@/features/auth/pages/NotFoundPage'
import PrivacyPage from '@/features/auth/pages/PrivacyPage'
import TermsPage from '@/features/auth/pages/TermsPage'
import HelpPage from '@/features/auth/pages/HelpPage'

// Client feature pages (contract §6 — parallel workstreams)
import TodayPage from '@/features/today/TodayPage'
import RememberPage from '@/features/reminders/RememberPage'
import PlannerPage from '@/features/planner/PlannerPage'
import BreathingPage from '@/features/breathing/BreathingPage'
import JournalPage from '@/features/journal/JournalPage'
import HealthPage from '@/features/health/HealthPage'
import PlacesPage from '@/features/places/PlacesPage'
import NotificationsPage from '@/features/notifications/NotificationsPage'
import MorePage from '@/features/more/MorePage'
import ProfilePage from '@/features/settings/ProfilePage'
import SecurityPage from '@/features/settings/SecurityPage'
import AccessibilityPage from '@/features/settings/AccessibilityPage'
import DataPage from '@/features/settings/DataPage'
import InstallPage from '@/features/settings/InstallPage'

// Caregiver portal pages (contract §6 — parallel workstreams)
import DashboardPage from '@/features/caregiver/DashboardPage'
import ClientsPage from '@/features/caregiver/ClientsPage'
import ClientDetailPage from '@/features/caregiver/ClientDetailPage'
import ClientSchedulePage from '@/features/caregiver/ClientSchedulePage'
import ClientIntakesPage from '@/features/caregiver/ClientIntakesPage'
import ClientProofsPage from '@/features/caregiver/ClientProofsPage'
import CaregiverProfilePage from '@/features/caregiver/CaregiverProfilePage'
import CaregiverSecurityPage from '@/features/caregiver/CaregiverSecurityPage'
import CaregiverAccessibilityPage from '@/features/caregiver/CaregiverAccessibilityPage'

/** '/' → welcome, or straight into the right portal when a session exists. */
function RootRedirect() {
  const { status, role } = useAuth()
  if (status === 'loading') return <SessionLoadingScreen />
  if (status === 'unauthenticated' || !role) return <Navigate to={PATHS.welcome} replace />
  return <Navigate to={portalHomeForRole(role)} replace />
}

/** Auth pages bounce already-authenticated users to their own portal. */
function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status, role } = useAuth()
  if (status === 'authenticated' && role) {
    return <Navigate to={portalHomeForRole(role)} replace />
  }
  return <>{children}</>
}

function ClientShell() {
  return (
    <RequireAuth role="client">
      <AppShell variant="client">
        <Outlet />
      </AppShell>
    </RequireAuth>
  )
}

function CaregiverShell() {
  return (
    <RequireAuth role="caregiver">
      <AppShell variant="caregiver">
        <Outlet />
      </AppShell>
    </RequireAuth>
  )
}

export function AppRouter() {
  return (
    <Routes>
      {/* Root dispatch */}
      <Route path={PATHS.root} element={<RootRedirect />} />

      {/* Public marketing / onboarding flow */}
      <Route path={PATHS.welcome} element={<WelcomePage />} />
      <Route path={PATHS.onboarding} element={<OnboardingPage />} />
      <Route path={PATHS.role} element={<RolePage />} />

      {/* Auth pages */}
      <Route
        path={PATHS.auth.clientLogin}
        element={
          <RedirectIfAuthenticated>
            <ClientLoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path={PATHS.auth.clientRegister}
        element={
          <RedirectIfAuthenticated>
            <ClientRegisterPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path={PATHS.auth.caregiverLogin}
        element={
          <RedirectIfAuthenticated>
            <CaregiverLoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path={PATHS.auth.caregiverRegister}
        element={
          <RedirectIfAuthenticated>
            <CaregiverRegisterPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route path={PATHS.auth.forgotPassword} element={<ForgotPasswordPage />} />
      <Route path={PATHS.auth.resetPassword} element={<ResetPasswordPage />} />
      <Route path={PATHS.auth.callback} element={<AuthCallbackPage />} />

      {/* Legal & support */}
      <Route path={PATHS.legal.privacy} element={<PrivacyPage />} />
      <Route path={PATHS.legal.terms} element={<TermsPage />} />
      <Route path={PATHS.legal.help} element={<HelpPage />} />

      {/* Authenticated client portal */}
      <Route element={<ClientShell />}>
        <Route path={PATHS.app.today} element={<TodayPage />} />
        <Route path={PATHS.app.remember} element={<RememberPage />} />
        <Route path={PATHS.app.planner} element={<PlannerPage />} />
        <Route path={PATHS.app.breathing} element={<BreathingPage />} />
        <Route path={PATHS.app.journal} element={<JournalPage />} />
        <Route path={PATHS.app.health} element={<HealthPage />} />
        <Route path={PATHS.app.places} element={<PlacesPage />} />
        <Route path={PATHS.app.notifications} element={<NotificationsPage />} />
        <Route path={PATHS.app.more} element={<MorePage />} />
        <Route path={PATHS.app.profile} element={<ProfilePage />} />
        <Route path={PATHS.app.security} element={<SecurityPage />} />
        <Route path={PATHS.app.accessibility} element={<AccessibilityPage />} />
        <Route path={PATHS.app.data} element={<DataPage />} />
        <Route path={PATHS.app.install} element={<InstallPage />} />
      </Route>

      {/* Authenticated caregiver portal */}
      <Route element={<CaregiverShell />}>
        <Route path={PATHS.caregiver.dashboard} element={<DashboardPage />} />
        <Route path={PATHS.caregiver.clients} element={<ClientsPage />} />
        <Route path={PATHS.caregiver.clientDetailPattern} element={<ClientDetailPage />} />
        <Route path={PATHS.caregiver.clientSchedulePattern} element={<ClientSchedulePage />} />
        <Route path={PATHS.caregiver.clientIntakesPattern} element={<ClientIntakesPage />} />
        <Route path={PATHS.caregiver.clientProofsPattern} element={<ClientProofsPage />} />
        <Route path={PATHS.caregiver.notifications} element={<NotificationsPage />} />
        <Route path={PATHS.caregiver.profile} element={<CaregiverProfilePage />} />
        <Route path={PATHS.caregiver.security} element={<CaregiverSecurityPage />} />
        <Route path={PATHS.caregiver.accessibility} element={<CaregiverAccessibilityPage />} />
      </Route>

      {/* Legacy pre-rebuild URLs */}
      {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}

      {/* Unknown routes */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
